import { isEqual } from "lodash";
import { JsonPatchOperation } from "_shared/_api/attorney-hub-api-service";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";

export default class UpdateIndividualProfileRequest {
  private static compareAndPush(
    payload: JsonPatchOperation[],
    updated: IndividualProfile,
    original: IndividualProfile,
    key: keyof IndividualProfile,
  ): void {
    if (updated[key] !== original[key]) {
      payload.push({ op: "replace", path: `/${key}`, value: updated[key] });
    }
  }

  private static deepCompareAndPush(
    payload: JsonPatchOperation[],
    updated: IndividualProfile,
    original: IndividualProfile,
    key: keyof IndividualProfile,
  ): void {
    if (!isEqual(updated[key], original[key])) {
      payload.push({ op: "replace", path: `/${key}`, value: updated[key] });
    }
  }

  public static buildPayload(
    isVisible?: boolean,
    originalProfile?: IndividualProfile,
    updatedProfile?: IndividualProfile,
  ): JsonPatchOperation[] {
    const payload: JsonPatchOperation[] = [];

    if (isVisible !== undefined) {
      payload.push({ op: "replace", path: "/isVisible", value: isVisible });
    }

    if (!originalProfile || !updatedProfile) {
      return payload;
    }

    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "firstName");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "lastName");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "professionalRegistration");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "credentials");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "isOnCall");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "school");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "degree");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "numberOfAllowedApplications");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "barAdmissionYear");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "potentialDiversityCandidate");
    UpdateIndividualProfileRequest.compareAndPush(payload, updatedProfile, originalProfile, "collaborates");

    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "phoneNumber");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "location");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "categories");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "regionsRegistered");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "fluentLanguages");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "technicalLanguages");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "regionsLicensed");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "sampleApplications");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "professionalPublications");
    UpdateIndividualProfileRequest.deepCompareAndPush(payload, updatedProfile, originalProfile, "links");

    if (!updatedProfile.email?.isEqualTo(originalProfile.email)) {
      payload.push({ op: "replace", path: "/email", value: updatedProfile.email?.value ?? "" });
    }

    if (updatedProfile.numberOfApplicationsDraftedProsecuted !== originalProfile.numberOfApplicationsDraftedProsecuted) {
      payload.push({
        op: "replace",
        path: "/numberOfApplicationsDraftedProsecuted",
        value: updatedProfile.numberOfApplicationsDraftedProsecuted,
      });
    }

    if (updatedProfile.technicalBackground !== originalProfile.technicalBackground) {
      payload.push({ op: "replace", path: "/technicalBackground", value: updatedProfile.technicalBackground?.value });
    }

    if (updatedProfile.insured?.insuredAmount !== originalProfile.insured?.insuredAmount) {
      payload.push({ op: "replace", path: "/insuredAmount", value: updatedProfile.insured?.insuredAmount });
    }

    if (updatedProfile.billingRate?.billingRate !== originalProfile.billingRate?.billingRate) {
      payload.push({ op: "replace", path: "/billingRate", value: updatedProfile.billingRate?.billingRate });
    }

    if (updatedProfile.billingRate?.billingUnit !== originalProfile.billingRate?.billingUnit) {
      payload.push({ op: "replace", path: "/billingUnit", value: updatedProfile.billingRate?.billingUnit });
    }

    return payload;
  }
}
